import {
  enrichInstallmentWithDiscountMetrics,
  extractCommissionInstallments,
} from "./detail/commissionInstallmentUtils";
import { normalizeSavedCommissionPlan } from "./detail/commissionPlanUtils";

function roundMoney(value) {
  return Math.round(Number(value) * 100) / 100;
}

export function resolvePlanStudentName(planRaw = {}) {
  const client = planRaw.client;
  if (client && typeof client === "object") {
    const first =
      client.firstname || client.first_name || client.other_details?.firstname || "";
    const last =
      client.lastname || client.last_name || client.other_details?.lastname || "";
    const full = [first, last].filter(Boolean).join(" ").trim();
    if (full) return full;
    if (client.name) return String(client.name);
    if (client.clientname) return String(client.clientname);
  }
  return (
    planRaw.client_name ||
    planRaw.student_name ||
    planRaw.clientName ||
    planRaw.studentName ||
    ""
  );
}

export function resolvePlanApplicationName(planRaw = {}, plan = {}) {
  const course = planRaw.course;
  if (course && typeof course === "object") {
    const label = course.name || course.coursename || course.course_name;
    if (label) return String(label);
  }
  const app = planRaw.application;
  if (app && typeof app === "object") {
    const fromApp =
      app.coursename ||
      app.course_name ||
      app.packagename ||
      app.package_name ||
      app.application_name;
    if (fromApp) return String(fromApp);
    const nested = app.course;
    if (nested && typeof nested === "object") {
      const nestedLabel = nested.name || nested.coursename || nested.course_name;
      if (nestedLabel) return String(nestedLabel);
    }
  }
  const provider = planRaw.provider;
  const providerName =
    provider && typeof provider === "object"
      ? provider.name || provider.provider_name
      : "";
  const courseBits = [
    planRaw.course_name || planRaw.coursename,
    providerName,
  ].filter(Boolean);
  if (courseBits.length) return courseBits.join(" · ");
  if (plan.applicationId) return `Application #${plan.applicationId}`;
  return "";
}

/** Tuition / student fee still owed on this installment. */
export function resolveInstallmentFeeDue(row = {}) {
  const balanceFee = row.balanceFee ?? row.balance_fee;
  if (balanceFee != null && balanceFee !== "") {
    return roundMoney(Math.max(0, Number(balanceFee) || 0));
  }
  if (row.tuitionPaid) return 0;
  const tuition = Number(row.tuitionAmount ?? row.tuition_amount) || 0;
  const paid = Number(row.studentPaidTuition ?? row.student_paid_tuition) || 0;
  const due = roundMoney(tuition - paid);
  if (due > 0) return due;
  return tuition > 0 ? roundMoney(tuition) : 0;
}

/** Commission only (excludes tuition / invoice allocatable totals). */
export function resolveInstallmentCommissionDue(row = {}) {
  const balance = Number(row.balanceCommission ?? row.balance_commission);
  if (Number.isFinite(balance) && balance > 0) return roundMoney(balance);

  const amount = Number(row.commissionAmount ?? row.commission_amount) || 0;
  const received =
    Number(row.commissionReceived ?? row.commission_received) || 0;
  const netDue = roundMoney(amount - received);
  if (netDue > 0) return netDue;
  if (amount > 0) return roundMoney(amount);
  return 0;
}

export const DUE_FILTER_OPTIONS = [
  { id: "all", label: "All" },
  { id: "overdue", label: "Overdue" },
  { id: "next_week", label: "Next week" },
  { id: "next_month", label: "Next Month" },
  { id: "custom", label: "Custom Date" },
];

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function parseInstallmentDueDate(value) {
  if (!value) return null;
  const text = String(value).trim();
  if (!text) return null;
  const iso = /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : text;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return startOfDay(parsed);
}

function endOfWeekSunday(fromDate) {
  const d = startOfDay(fromDate);
  const day = d.getDay();
  const daysUntilSunday = day === 0 ? 0 : 7 - day;
  const end = new Date(d);
  end.setDate(end.getDate() + daysUntilSunday);
  return end;
}

function startOfNextMonth(date) {
  const d = startOfDay(date);
  return startOfDay(new Date(d.getFullYear(), d.getMonth() + 1, 1));
}

function endOfNextMonth(date) {
  const d = startOfDay(date);
  return startOfDay(new Date(d.getFullYear(), d.getMonth() + 2, 0));
}

function startOfNextWeek(fromDate) {
  const weekEnd = endOfWeekSunday(fromDate);
  const start = new Date(weekEnd);
  start.setDate(start.getDate() + 1);
  return startOfDay(start);
}

function endOfNextWeek(fromDate) {
  const start = startOfNextWeek(fromDate);
  return endOfWeekSunday(start);
}

export function matchesDueFilter(
  dueDate,
  filterId,
  today = new Date(),
  customRange = null,
) {
  if (filterId === "all") return true;
  if (filterId === "custom") {
    const startRaw = customRange?.startDate ?? customRange?.start;
    const endRaw = customRange?.endDate ?? customRange?.end;
    if (!startRaw && !endRaw) return true;
    if (!dueDate) return false;
    const due = parseInstallmentDueDate(dueDate);
    if (!due) return false;
    const rangeStart = startRaw ? parseInstallmentDueDate(startRaw) : null;
    const rangeEnd = endRaw ? parseInstallmentDueDate(endRaw) : null;
    if (rangeStart && due < rangeStart) return false;
    if (rangeEnd && due > rangeEnd) return false;
    return true;
  }
  if (!dueDate) return false;

  const now = startOfDay(today);
  const due = parseInstallmentDueDate(dueDate);
  if (!due) return false;

  if (filterId === "overdue") {
    return due < now;
  }
  if (filterId === "next_week") {
    const nextWeekStart = startOfNextWeek(now);
    const nextWeekEnd = endOfNextWeek(now);
    return due >= nextWeekStart && due <= nextWeekEnd;
  }
  if (filterId === "next_month") {
    const nextStart = startOfNextMonth(now);
    const nextEnd = endOfNextMonth(now);
    return due >= nextStart && due <= nextEnd;
  }
  return true;
}

/** Installments not fully paid / received (read-only mirror of detail card labels). */
export function isAwaitingPaymentInstallment(row = {}) {
  const status = String(row?.status || "").toLowerCase();
  if (status === "paid" || status === "cancelled" || status === "void") {
    return false;
  }
  const label = String(row?.commissionStatusLabel || "").toLowerCase();
  if (label.includes("received 100")) return false;
  const pct = Number(row?.commissionReceivedPercent) || 0;
  if (pct >= 100) return false;
  return true;
}

export function buildCommissionDetailPathFromPlan(plan = {}) {
  const clientId = plan.clientId ?? plan.client_id;
  const applicationId = plan.applicationId ?? plan.application_id;
  const providerId = plan.providerId ?? plan.provider_id;
  const courseId = plan.courseId ?? plan.course_id;
  const applicationCourseId =
    plan.applicationCourseId ?? plan.application_course_id;
  const planId = plan.id ?? plan.planId;

  if (!clientId || !applicationId || !providerId) return null;

  const params = new URLSearchParams({
    student_id: String(clientId),
    app_id: String(applicationId),
    provider_id: String(providerId),
    type: "education",
    tab: "installments",
  });
  if (courseId) params.set("course_id", String(courseId));
  if (applicationCourseId) {
    params.set("application_course_id", String(applicationCourseId));
  }
  if (planId) params.set("planId", String(planId));
  return `/commissions/detail?${params.toString()}`;
}

export function flattenCommissionPlansToInstallmentRows(plans = []) {
  const rows = [];
  (Array.isArray(plans) ? plans : []).forEach((planRaw) => {
    const plan = normalizeSavedCommissionPlan(planRaw) || {};
    const installments = extractCommissionInstallments(planRaw);
    const detailPath = buildCommissionDetailPathFromPlan({
      ...plan,
      id: plan.id,
    });

    const studentName = resolvePlanStudentName(planRaw);
    const applicationName = resolvePlanApplicationName(planRaw, plan);

    installments.forEach((installment) => {
      const enriched = enrichInstallmentWithDiscountMetrics(installment, plan);
      rows.push({
        ...enriched,
        planId: plan.id,
        clientId: plan.clientId,
        applicationId: plan.applicationId,
        providerId: plan.providerId,
        courseId: plan.courseId,
        applicationCourseId: plan.applicationCourseId,
        currency: plan.currency || "AUD",
        studentName,
        applicationName,
        feeDue: resolveInstallmentFeeDue(enriched),
        commissionDue: resolveInstallmentCommissionDue(enriched),
        detailPath,
      });
    });
  });
  return rows;
}

export function sortInstallmentsByDueDate(rows = []) {
  return [...rows].sort((a, b) => {
    const da = parseInstallmentDueDate(a.dueDate);
    const db = parseInstallmentDueDate(b.dueDate);
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return da - db;
  });
}

export function countDueBuckets(rows = [], today = new Date()) {
  const counts = {
    overdue: 0,
    next_week: 0,
    next_month: 0,
    total: rows.length,
  };
  rows.forEach((row) => {
    if (matchesDueFilter(row.dueDate, "overdue", today)) counts.overdue += 1;
    if (matchesDueFilter(row.dueDate, "next_week", today)) counts.next_week += 1;
    if (matchesDueFilter(row.dueDate, "next_month", today)) {
      counts.next_month += 1;
    }
  });
  return counts;
}

export function formatDueDateLabel(value) {
  const d = parseInstallmentDueDate(value);
  if (!d) return "—";
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function resolveDueUrgencyTone(dueDate, today = new Date()) {
  if (!dueDate) return "slate";
  if (matchesDueFilter(dueDate, "overdue", today)) return "red";
  if (matchesDueFilter(dueDate, "next_week", today)) return "amber";
  return "slate";
}
